const DEFAULT_DIRECTION = 'down';
const EMPTY_COUNTDOWN_PARTS = {
	days: '0',
	hours: '0',
	minutes: '0',
	seconds: '0',
	hasTarget: false,
};

/**
 * Normalize countdown direction.
 *
 * @param {string} value Raw direction value.
 * @return {string} Supported direction value.
 */
const normalizeCountdownDirection = ( value ) => value === 'up' ? 'up' : DEFAULT_DIRECTION;

/**
 * Parse a stored local date string into a Date object.
 *
 * @param {string} value Stored date value.
 * @return {Date|null} Parsed date, or null when invalid.
 */
const parseLocalDateTime = ( value ) => {

	if ( ! value ) {

		return null;

	}

	const match = String( value )
		.trim()
		.match( /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/ );

	if ( ! match ) {

		return null;

	}

	const [ , year, month, day, hours, minutes, seconds = '00' ] = match;
	const parsedDate = new Date(
		Number( year ),
		Number( month ) - 1,
		Number( day ),
		Number( hours ),
		Number( minutes ),
		Number( seconds )
	);

	if ( Number.isNaN( parsedDate.getTime() ) ) {

		return null;

	}

	return parsedDate;

};

/**
 * Build the displayed countdown values.
 *
 * @param {string} targetDateTime Stored target date string.
 * @param {string} direction      Countdown direction.
 * @param {number} [now]          Current timestamp in milliseconds.
 * @return {object} Countdown parts ready for display.
 */
const getCountdownParts = ( targetDateTime, direction, now = Date.now() ) => {

	const targetDate = parseLocalDateTime( targetDateTime );
	if ( ! targetDate ) {

		return EMPTY_COUNTDOWN_PARTS;

	}

	const normalizedDirection = normalizeCountdownDirection( direction );
	const deltaMilliseconds = normalizedDirection === 'up'
		? now - targetDate.getTime()
		: targetDate.getTime() - now;
	const safeSeconds = Math.max( Math.floor( deltaMilliseconds / 1000 ), 0 );
	const days = Math.floor( safeSeconds / 86400 );
	const hours = Math.floor( ( safeSeconds % 86400 ) / 3600 );
	const minutes = Math.floor( ( safeSeconds % 3600 ) / 60 );
	const seconds = safeSeconds % 60;

	return {
		days: String( days ),
		hours: String( hours ),
		minutes: String( minutes ),
		seconds: String( seconds ),
		hasTarget: true,
	};

};

/**
 * Check whether a countdown value is zero and should be hidden.
 *
 * @param {string} value Countdown value.
 * @return {boolean} True when the whole tile should be hidden.
 */
const isCountdownValueZero = ( value ) => Number( value || 0 ) === 0;

/**
 * Check whether the countdown has finished.
 *
 * @param {string} targetDateTime Stored target date string.
 * @param {string} direction      Countdown direction.
 * @param {number} [now]          Current timestamp in milliseconds.
 * @return {boolean} True when the countdown has reached its end state.
 */
const isCountdownFinished = ( targetDateTime, direction, now = Date.now() ) => {

	const targetDate = parseLocalDateTime( targetDateTime );
	if ( ! targetDate ) {

		return false;

	}

	return normalizeCountdownDirection( direction ) === 'down'
		? now >= targetDate.getTime()
		: now <= targetDate.getTime();

};

/**
 * Get countdown unit keys that should be hidden for zero-value units.
 *
 * @param {object} parts Countdown parts object.
 * @return {string[]} Unit keys that should be hidden.
 */
const getHiddenCountdownUnits = ( parts ) => {

	const orderedUnits = [ 'days', 'hours', 'minutes', 'seconds' ];

	return orderedUnits.filter(
		( unit, index ) => index < orderedUnits.length - 1 && isCountdownValueZero( parts[ unit ] )
	);

};

/**
 * Normalize a countdown value to a safe positive integer.
 *
 * @param {string|number} value Raw countdown value.
 * @return {number} Normalized integer.
 */
const normalizeCountdownValue = ( value ) => {

	const parsedValue = Number.parseInt( value, 10 );

	if ( Number.isNaN( parsedValue ) ) {

		return 0;

	}

	return Math.abs( parsedValue );

};

/**
 * Parse localized unit labels from the block dataset.
 *
 * @param {string} value Serialized JSON labels.
 * @return {Object<string, Object<string, string>>} Safe label map.
 */
const parseCountdownUnitLabels = ( value ) => {

	if ( ! value ) {

		return {};

	}

	try {

		return JSON.parse( value );

	} catch ( error ) {

		return {};

	}

};

/**
 * Resolve the plural category for a countdown value.
 *
 * @param {string|number} value Countdown value.
 * @param {string} locale Current locale.
 * @return {string} Intl plural category.
 */
const getPluralCategory = ( value, locale ) => {

	const normalizedValue = normalizeCountdownValue( value );

	if ( typeof Intl !== 'undefined' && typeof Intl.PluralRules === 'function' ) {

		try {

			return new Intl.PluralRules( locale ).select( normalizedValue );

		} catch ( error ) {

			return normalizedValue === 1 ? 'one' : 'other';

		}

	}

	return normalizedValue === 1 ? 'one' : 'other';

};

/**
 * Resolve a localized label for a countdown unit.
 *
 * @param {string} unit Unit key.
 * @param {string|number} value Countdown value.
 * @param {Object<string, Object<string, string>>} labelsByUnit Labels from PHP.
 * @param {string} locale Current locale.
 * @return {string} Localized label.
 */
const getCountdownUnitLabel = ( unit, value, labelsByUnit, locale ) => {

	const unitLabels = labelsByUnit?.[ unit ] || {};
	const pluralCategory = getPluralCategory( value, locale );

	return unitLabels[ pluralCategory ]
		|| unitLabels.few
		|| unitLabels.one
		|| unitLabels.other
		|| '';

};

/**
 * Update the values rendered by a single countdown block.
 *
 * @param {HTMLElement} block Countdown block element.
 * @return {void}
 */
const updateCountdownBlock = ( block ) => {

	const direction = normalizeCountdownDirection( block.dataset.countdownDirection );
	const expiredBehavior = block.dataset.countdownExpiredBehavior || 'hide-block';
	const countdownLocale = block.dataset.countdownLocale || document?.documentElement?.lang || 'en';
	const countdownUnitLabels = block.countdownUnitLabels || parseCountdownUnitLabels( block.dataset.countdownUnitLabels );
	const parts = getCountdownParts( block.dataset.countdownTarget || '', direction );
	const hiddenUnits = getHiddenCountdownUnits( parts );
	const countdownItems = block.querySelectorAll( '[data-countdown-unit]' );
	const countdownElement = block.querySelector( '.b-countdown-banner__countdown' );
	const expiredContentElement = block.querySelector( '.b-countdown-banner__expired-content' );
	const hasExpired = direction === 'down' && isCountdownFinished( block.dataset.countdownTarget || '', direction );
	const shouldHideBlock = hasExpired && expiredBehavior === 'hide-block';

	block.countdownUnitLabels = countdownUnitLabels;

	block.hidden = shouldHideBlock;
	block.style.display = shouldHideBlock ? 'none' : '';

	if ( countdownElement ) {

		countdownElement.hidden = hasExpired && expiredBehavior === 'replace-content';

	}

	if ( expiredContentElement ) {

		expiredContentElement.hidden = ! hasExpired || expiredBehavior !== 'replace-content';
		expiredContentElement.style.display = expiredContentElement.hidden ? 'none' : 'block';

	}

	countdownItems.forEach( ( item ) => {

		const unit = item.dataset.countdownUnit;
		const valueElement = item.querySelector( '[data-countdown-value]' );

		if ( ! valueElement ) {

			return;

		}

		const labelElement = item.querySelector( '[data-countdown-label]' );
		const nextValue = parts[ unit ] || '0';
		const nextLabel = getCountdownUnitLabel( unit, nextValue, countdownUnitLabels, countdownLocale );

		if ( valueElement.textContent !== nextValue ) {

			valueElement.textContent = nextValue;

		}

		item.hidden = hasExpired || hiddenUnits.includes( unit );
		item.style.display = item.hidden ? 'none' : 'flex';

		if ( labelElement ) {

			if ( labelElement.textContent !== nextLabel ) {

				labelElement.textContent = nextLabel;

			}

			item.setAttribute( 'aria-label', `${ nextValue } ${ nextLabel }` );

		}

	} );

};

document.querySelectorAll( '.b-countdown-banner' ).forEach( ( block ) => {

	if ( block.dataset.countdownReady === 'true' ) {

		return;

	}

	updateCountdownBlock( block );

	const intervalId = window.setInterval( () => {

		updateCountdownBlock( block );

	}, 1000 );

	block.dataset.countdownReady = 'true';
	block.addEventListener( 'remove', () => {

		window.clearInterval( intervalId );

	} );

} );
